/**
 * smarte Header menu — hover + animated open/close on desktop
 */
(function () {
  const DESKTOP_MQ = window.matchMedia('(min-width: 990px)');
  const CLOSE_DELAY_MS = 200;

  class SmarteHeaderMenuController {
    constructor() {
      this.wrapper = document.querySelector('.smarte-header__wrapper');
      if (!this.wrapper) return;

      this.items = this.wrapper.querySelectorAll('.smarte-header__menu-item');
      this.closeTimers = new WeakMap();
      this.activeDetails = null;

      this.items.forEach((item) => {
        const details = item.querySelector('details');
        if (!details) return;

        const trigger = details.querySelector('.smarte-header__menu-trigger');
        const panel = details.querySelector('.smarte-header__mega-panel');
        const submenu = details.querySelector('.smarte-header__submenu');

        if (trigger) {
          trigger.addEventListener('mouseenter', () => this.onMouseEnter(details));
        }

        [panel, submenu].filter(Boolean).forEach((target) => {
          target.addEventListener('mouseenter', () => this.onMouseEnter(details));
          target.addEventListener('mouseleave', () => this.onMouseLeave(details));
        });

        item.addEventListener('mouseleave', (event) => {
          if (panel && panel.contains(event.relatedTarget)) return;
          if (submenu && submenu.contains(event.relatedTarget)) return;
          this.onMouseLeave(details);
        });

        details.addEventListener('toggle', () => this.onToggle(details));
      });

      document.addEventListener('click', (event) => {
        if (!this.wrapper.contains(event.target)) {
          this.closeAll();
        }
      });

      DESKTOP_MQ.addEventListener('change', () => this.closeAll());

      this.onSearchReposition = this.updateHeaderMetrics.bind(this);
      this.onMenuReposition = this.updateHeaderMetrics.bind(this);

      const searchDetails = this.wrapper.querySelector('.smarte-header__search details');
      if (searchDetails) {
        searchDetails.addEventListener('toggle', () => {
          if (searchDetails.hasAttribute('open')) {
            this.updateHeaderMetrics();
            this.bindSearchReposition();
            this.closeAll();
          } else {
            this.unbindSearchReposition();
          }
        });
      }
    }

    updateHeaderMetrics() {
      const header = document.querySelector('.section-smarte-header');
      if (!header) return;

      const rect = header.getBoundingClientRect();
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      const isStuck =
        header.classList.contains('shopify-section-header-sticky') &&
        rect.top <= 0 &&
        scrollTop > 0;

      document.documentElement.style.setProperty('--header-height', `${rect.height}px`);
      document.documentElement.style.setProperty('--header-offset-top', `${rect.top}px`);
      header.classList.toggle('smarte-header--compact', isStuck);
    }

    bindMenuReposition() {
      window.addEventListener('scroll', this.onMenuReposition, { passive: true });
      window.addEventListener('resize', this.onMenuReposition);
    }

    unbindMenuReposition() {
      window.removeEventListener('scroll', this.onMenuReposition);
      window.removeEventListener('resize', this.onMenuReposition);
    }

    bindSearchReposition() {
      window.addEventListener('resize', this.onSearchReposition);
    }

    unbindSearchReposition() {
      window.removeEventListener('resize', this.onSearchReposition);
    }

    onMouseEnter(details) {
      if (!DESKTOP_MQ.matches) return;

      this.clearAllCloseTimers();
      this.open(details);
    }

    onMouseLeave(details) {
      if (!DESKTOP_MQ.matches) return;

      this.scheduleClose(details);
    }

    onToggle(details) {
      const summary = details.querySelector('summary');
      const isOpen = details.hasAttribute('open');

      if (summary) {
        summary.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
      }

      if (isOpen) {
        this.clearAllCloseTimers();
        this.closeAllExcept(details);
        this.activeDetails = details;
      } else if (this.activeDetails === details) {
        this.activeDetails = null;
      }

      this.syncWrapperState();
    }

    open(details) {
      if (this.activeDetails === details && details.hasAttribute('open')) {
        return;
      }

      this.updateHeaderMetrics();
      this.closeAllExcept(details);
      details.setAttribute('open', '');
      this.activeDetails = details;

      const summary = details.querySelector('summary');
      if (summary) {
        summary.setAttribute('aria-expanded', 'true');
      }

      this.syncWrapperState();
    }

    close(details) {
      details.removeAttribute('open');

      const summary = details.querySelector('summary');
      if (summary) {
        summary.setAttribute('aria-expanded', 'false');
      }

      if (this.activeDetails === details) {
        this.activeDetails = null;
      }

      this.syncWrapperState();
    }

    scheduleClose(details) {
      this.clearCloseTimer(details);

      const timerId = window.setTimeout(() => {
        if (this.activeDetails === details) {
          this.close(details);
        }
        this.closeTimers.delete(details);
      }, CLOSE_DELAY_MS);

      this.closeTimers.set(details, timerId);
    }

    clearCloseTimer(details) {
      const timerId = this.closeTimers.get(details);
      if (timerId) {
        window.clearTimeout(timerId);
        this.closeTimers.delete(details);
      }
    }

    clearAllCloseTimers() {
      this.items.forEach((item) => {
        const details = item.querySelector('details');
        if (details) {
          this.clearCloseTimer(details);
        }
      });
    }

    closeAllExcept(activeDetails) {
      this.items.forEach((item) => {
        const details = item.querySelector('details');
        if (!details || details === activeDetails) return;

        this.clearCloseTimer(details);
        this.close(details);
      });
    }

    closeAll() {
      this.items.forEach((item) => {
        const details = item.querySelector('details');
        if (!details) return;

        this.clearCloseTimer(details);
        this.close(details);
      });
    }

    syncWrapperState() {
      const anyOpen = Array.from(this.items).some((item) => {
        const details = item.querySelector('details');
        return details && details.hasAttribute('open');
      });

      this.wrapper.classList.toggle('smarte-header__wrapper--menu-open', anyOpen);

      if (anyOpen) {
        this.updateHeaderMetrics();
        this.bindMenuReposition();
      } else {
        this.unbindMenuReposition();
      }
    }
  }

  function init() {
    if (document.querySelector('.smarte-header__wrapper[data-menu-init]')) return;

    const wrapper = document.querySelector('.smarte-header__wrapper');
    if (wrapper) {
      wrapper.setAttribute('data-menu-init', '');
    }

    const controller = new SmarteHeaderMenuController();
    controller.updateHeaderMetrics();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', (event) => {
    if (!event.target.querySelector('.smarte-header__wrapper')) return;

    const wrapper = event.target.querySelector('.smarte-header__wrapper');
    wrapper?.removeAttribute('data-menu-init');
    init();
  });
})();
